// FILE: managedAgentsReactQuery.ts
// Purpose: React Query wrapper around `AgentsClient.listVisible()` for the
//          composer's provider/model picker. Mirrors the shape of
//          `providerDiscoveryReactQuery.ts` (query key + `queryOptions`
//          factory) rather than TeamsSection's manual fetch: ChatView already
//          reads every other piece of picker data (models, provider-native
//          agents, capabilities) through `useQuery`/`queryOptions`, so this
//          keeps caching, refetch-on-focus, and staleness consistent with the
//          rest of the composer instead of introducing a second data-fetching
//          idiom next to it.
// Layer: Web client
// Exports: `managedAgentsQueryKey`, `managedAgentsQueryOptions`,
//          `useVisibleManagedAgentsById`.

import type { ManagedAgent } from "@taskin-code/contracts";
import { queryOptions, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";

import { AgentsClient } from "./AgentsClient";

const EMPTY_AGENTS: ReadonlyArray<ManagedAgent> = [];

export const managedAgentsQueryKey = ["managed-agents", "visible"] as const;

export function managedAgentsQueryOptions(input?: { enabled?: boolean }) {
  return queryOptions({
    queryKey: managedAgentsQueryKey,
    queryFn: () => AgentsClient.listVisible(),
    enabled: input?.enabled ?? true,
    // Visibility depends on team membership and admin edits, neither of which
    // changes as often as model/skill discovery — a minute-long stale window
    // matches `providerModelsQueryOptions`.
    staleTime: 60_000,
    placeholderData: (previous) => previous ?? EMPTY_AGENTS,
  });
}

/**
 * Visible agents keyed by id for surfaces that begin with a persisted
 * `agentId` rather than a full catalog record (the sidebar and thread
 * directory). Every caller subscribes to the same React Query entry above,
 * so a list of rows still makes one request. Before it resolves this is an
 * empty map: `AgentAvatar` receives no file name and immediately paints its
 * initials fallback, never an empty image slot.
 */
export function useVisibleManagedAgentsById(): ReadonlyMap<string, ManagedAgent> {
  const query = useQuery(managedAgentsQueryOptions());
  return useMemo(
    () => new Map((query.data ?? EMPTY_AGENTS).map((agent) => [agent.id, agent])),
    [query.data],
  );
}
